<template>
    <div class="flex flex-column xl:flex-row lg:flex-row md:flex-row px-3 py-2">
        <div class="flex flex-row align-items-center">
            <SelectButton v-model="selectedColumns" :options="columns" optionLabel="name" multiple aria-labelledby="multiple">
                <template #option="slotProps">
                    <span class="text-xs py-1 white-space-nowrap">{{ slotProps.option.name }}</span>
                </template>
            </SelectButton>
            <Button label="Обновить" icon="pi pi-sync" size="small" @click="LoadRecipients" class="border-round-lg border-0 ml-2" severity="info" outlined/>
            <Button label="Сбросить" icon="pi pi-filter" size="small" @click="resetFilter" class="border-round-lg border-0 ml-2" severity="info" outlined/>
        </div>
    </div>

    <DataTable
        v-if="list"
        :value="list"
        lazy
        paginator
        :rows="20"
        v-model:filters="filters"
        ref="dt"
        dataKey="id"
        :totalRecords="totalRecords"
        :loading="loading"
        @page="onPage($event)"
        @sort="onSort($event)"
        @filter="onFilter($event)"
        filterDisplay="menu"
        :globalFilterFields="[
            'id',
            'user_id',
            'currency_code',
            'bank_code',
            'payment_system_code',
            'fields.fullname',
            'fields.phone_number',
            'fields.card_number',
            'fields.account_number',
            'fields.wallet_address',
            'fields.wallet_network',
            'fields.telegram',
            'fields.bank_name',
            'fields.address',
            'fields.city',
            'fields.country',
            'fields.swift_code',
            'fields.iban_number',
            'fields.upi_id',
            'fields.wallet_id'
        ]"
        :rowsPerPageOptions="[5, 10, 20, 50, 100, 200, 300, 400, 500]"
    >

        <!-- ID получателя -->
        <Column field="id" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :exportable="false">
            <template #header>
                <span class="text-xs">ID</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()"
                    class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-2 align-items-center" v-if="data.id">
                    <span class="text-xs flex my-auto overflow-hidden" style="max-width:4em">
                        <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                            {{ data.id }}
                        </span>
                    </span>...
                    <Button icon="pi pi-copy" @click="copy(data.id)" size="small" class="border-round-md border-round-lg border-0" severity="info" outlined/>
                </div>
            </template>
        </Column>

        <!-- Пользователь (владелец получателя) -->
        <Column field="user_id" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
            <template #header>
                <span class="text-xs">Пользователь</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText
                    type="text"
                    v-model="filterModel.value"
                    @keydown.enter="filterCallback()"
                    class="p-column-filter w-12rem"
                    placeholder="Найти"
                />
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-1 align-items-center" v-if="data.user_id">
                    <span class="text-xs flex my-auto overflow-hidden" style="max-width:4em">
                        <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                            {{ data.user_id }}
                        </span>
                    </span>...
                    <Button
                        icon="pi pi-copy text-xs"
                        @click="copy(data.user_id)"
                        size="small"
                        class="border-round-md border-round-lg border-0"
                        severity="info"
                        outlined
                    />
                    <Button
                        icon="pi pi-external-link text-xs"
                        @click="detail_page(data.user_id, 'user', true)"
                        size="small"
                        class="border-round-md border-round-lg border-0"
                        severity="info"
                        outlined
                    />
                </div>
            </template>
        </Column>

        <!-- Валюта -->
        <Column field="currency_code" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
            <template #header>
                <span class="text-xs">Валюта</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-8rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs uppercase" v-if="data.currency_code">{{ data.currency_code }}</span>
            </template>
        </Column>

        <!-- Банк (код) -->
        <Column field="bank_code" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
            <template #header>
                <span class="text-xs">Банк (код)</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-8rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.bank_code">{{ data.bank_code }}</span>
            </template>
        </Column>

        <!-- Платежная система -->
        <Column field="payment_system_code" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
            <template #header>
                <span class="text-xs">Плат. система</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-10rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.payment_system_code">{{ data.payment_system_code }}</span>
            </template>
        </Column>

        <!-- Подтвержден -->
        <Column field="confirmed">
            <template #header>
                <span class="text-xs">Подтв.</span>
            </template>
            <template #body="{ data }">
                <span
                    class="text-xs px-2 py-1 border-round-md"
                    :class="data.confirmed ? 'bg-green-400 text-white' : 'surface-200 text-600'"
                >
                    {{ data.confirmed ? 'Да' : 'Нет' }}
                </span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <Dropdown
                    v-model="filterModel.value"
                    @change="filterCallback()"
                    :options="[
                        { label: 'Все', value: null },
                        { label: 'Да', value: true },
                        { label: 'Нет', value: false }
                    ]"
                    optionLabel="label"
                    optionValue="value"
                    class="p-column-filter w-10rem"
                />
            </template>
        </Column>

        <!-- Дата создания -->
        <Column field="date_created" sortable filterField="date_created" dataType="date" :showFilterMatchModes="false" :showFilterOperator="false" :showClearButton="true">
            <template #header>
                <span class="text-xs">Создан</span>
            </template>
            <template #filter="{ filterModel }">
                <Calendar v-model="filterModel.value" dateFormat="dd.mm.yy" placeholder="dd.mm.yy" mask="99.99.9999" />
            </template>
            <template #body="{ data }">
                <small class="text-xs white-space-nowrap">{{ formatDate(data.date_created) }}</small>
            </template>
        </Column>

        <Column :exportable="false" v-if="isUserInGroup('manager') || isUserInGroup('admin')">
            <template #header>
                <span class="text-xs">Действия</span>
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-2">
                    <Button
                        icon="pi pi-ban"
                        size="small"
                        class="border-round-md border-round-lg border-0"
                        severity="danger"
                        outlined
                        @click="openBlacklistAddPage(data)"
                    />
                    <Button
                        icon="pi pi-pencil"
                        size="small"
                        class="border-round-md border-round-lg border-0"
                        severity="info"
                        outlined
                        @click="openEditRecipientFields(data)"
                    />
                </div>
            </template>
        </Column>

        <!-- FIELDS.* -->

        <Column field="fields.fullname" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Имя</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.fields && data.fields.fullname">{{ data.fields.fullname }}</span>
            </template>
        </Column>

        <Column field="fields.phone_number" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Телефон</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-10rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.fields && data.fields.phone_number">{{ data.fields.phone_number }}</span>
            </template>
        </Column>

        <Column field="fields.telegram" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Telegram</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-1 align-items-center" v-if="data.fields && data.fields.telegram">
                    <span class="text-xs">{{ data.fields.telegram }}</span>
                    <Button icon="pi pi-telegram" @click="goToTelegram(data.fields.telegram)" size="small" class="border-round-md border-round-lg border-0" severity="info" outlined/>
                    <Button icon="pi pi-copy" @click="copy(data.fields.telegram)" size="small" class="border-round-md border-round-lg border-0" severity="info" outlined/>
                </div>
            </template>
        </Column>

        <Column field="fields.card_number" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Карта</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.fields && data.fields.card_number">{{ data.fields.card_number }}</span>
            </template>
        </Column>

        <Column field="fields.account_number" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Счет</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.fields && data.fields.account_number">{{ data.fields.account_number }}</span>
            </template>
        </Column>

        <Column field="fields.iban_number" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">IBAN</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.fields && data.fields.iban_number">{{ data.fields.iban_number }}</span>
            </template>
        </Column>

        <Column field="fields.swift_code" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">SWIFT</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-10rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.fields && data.fields.swift_code">{{ data.fields.swift_code }}</span>
            </template>
        </Column>

        <Column field="fields.wallet_address" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Кошелек</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-14rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.fields && data.fields.wallet_address">{{ data.fields.wallet_address }}</span>
            </template>
        </Column>

        <Column field="fields.wallet_network" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Сеть</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-10rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs uppercase" v-if="data.fields && data.fields.wallet_network">{{ data.fields.wallet_network }}</span>
            </template>
        </Column>

        <Column field="fields.upi_id" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">UPI ID</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.fields && data.fields.upi_id">{{ data.fields.upi_id }}</span>
            </template>
        </Column>

        <Column field="fields.bank_name" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Банк (имя)</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.fields && data.fields.bank_name">{{ data.fields.bank_name }}</span>
            </template>
        </Column>

        <Column field="fields.address" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Адрес</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-16rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.fields && data.fields.address">{{ data.fields.address }}</span>
            </template>
        </Column>

        <Column field="fields.city" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Город</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-10rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.fields && data.fields.city">{{ data.fields.city }}</span>
            </template>
        </Column>

        <Column field="fields.country" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Страна</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-10rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.fields && data.fields.country">{{ data.fields.country }}</span>
            </template>
        </Column>

        <Column field="fields.wallet_id" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" v-if="get_column('Реквизиты')">
            <template #header>
                <span class="text-xs">Wallet ID</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.fields && data.fields.wallet_id">{{ get_short_id(data.fields.wallet_id) }}</span>
            </template>
        </Column>

        <template #footer>
            <span class="text-sm">
                Всего {{ totalRecords }} получателей.
            </span>
        </template> 

    </DataTable>
    <Skeleton height="2rem" v-else />

    <Dialog
        header="Редактировать реквизиты"
        v-model:visible="editFieldsDialogVisible" @hide="closePopup"
        modal class="border-0"
        :breakpoints="{'960px': '75vw', '640px': '90vw'}"
        :style="{ width: '50vw' }"
    >
        <div v-if="currentRecipient" class="flex flex-column gap-3">
            <div class="text-sm surface-100 p-2 border-round-md">
                <div class="mb-1">ID получателя: <span>{{ currentRecipient.id }}</span></div>
                <div class="mb-1">Пользователь: <span>{{ currentRecipient.user_id }}</span></div>
                <div class="mb-1">Валюта: <span>{{ currentRecipient.currency_code }}</span></div>
                <div class="mb-1">Банк: <span>{{ currentRecipient.bank_code || '-' }}</span></div>
                <div class="mb-1">Платёжная система: <span>{{ currentRecipient.payment_system_code || '-' }}</span></div>
            </div>

            <RecipientFields
                v-if="dialogFields"
                :fields="dialogFields"
                :requiredFields="dialogRequiredFields"
                :currency="dialogCurrency.code"
                :initialFields="currentRecipient && currentRecipient.fields ? currentRecipient.fields : {}"
                :recipientId="currentRecipient.id"
                @check_valid="onRecipientFieldsValid"
                @return_fields="onRecipientFieldsChange"
            />

            <div v-else class="text-xs text-600">
                Для этого получателя не настроены реквизиты банка. Изменить нечего.
            </div>

            <div class="flex justify-content-end gap-2 mt-3">
                <Button
                    label="Отмена"
                    size="small"
                    class="border-round-lg border-0"
                    severity="secondary"
                    outlined
                    @click="closeEditFieldsDialog"
                />
                <Button
                    label="Сохранить"
                    size="small"
                    class="border-round-lg border-0"
                    severity="success"
                    :disabled="!recipientFieldsValid || savingFields || !dialogFields || !dialogFields.length"
                    :loading="savingFields"
                    @click="saveRecipientFields"
                />
            </div>
        </div>
    </Dialog>
</template>

<script>
import Connect from '@/assets/js/Connect'
import Helper from '@/assets/js/Helper'
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import RecipientFields from '@/components/widgets/RecipientFieldsEdit.vue'
import { BankList } from '@/assets/js/classes/PaymentSystem.js'

export default {
    name: 'RecipientList',
    data() {
        return {
            list: [],
            totalRecords: 0,
            loading: false,
            lazyParams: {},
            columns: [
                {name: 'Реквизиты', value: 'Реквизиты'},
            ],
            selectedColumns: [],
            filters: {
                id: { value: '', matchMode: 'contains' },
                user_id: { value: '', matchMode: 'contains' },
                currency_code: { value: '', matchMode: 'contains' },
                bank_code: { value: '', matchMode: 'contains' },
                payment_system_code: { value: '', matchMode: 'contains' },
                confirmed: { value: null, matchMode: FilterMatchMode.EQUALS },
                date_created: { 
                    operator: FilterOperator.AND, 
                    constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] 
                },
                'fields.fullname': { value: '', matchMode: 'contains' },
                'fields.phone_number': { value: '', matchMode: 'contains' },
                'fields.card_number': { value: '', matchMode: 'contains' },
                'fields.account_number': { value: '', matchMode: 'contains' },
                'fields.wallet_address': { value: '', matchMode: 'contains' },
                'fields.wallet_network': { value: '', matchMode: 'contains' },
                'fields.telegram': { value: '', matchMode: 'contains' },
                'fields.bank_name': { value: '', matchMode: 'contains' },
                'fields.address': { value: '', matchMode: 'contains' },
                'fields.city': { value: '', matchMode: 'contains' },
                'fields.country': { value: '', matchMode: 'contains' },
                'fields.swift_code': { value: '', matchMode: 'contains' },
                'fields.iban_number': { value: '', matchMode: 'contains' },
                'fields.upi_id': { value: '', matchMode: 'contains' },
                'fields.wallet_id': { value: '', matchMode: 'contains' },
            },
            sort: {
                field: 'date_created',
                order: -1
            },
            editFieldsDialogVisible: false,
            currentRecipient: null,
            dialogFields: [],
            dialogRequiredFields: [],
            dialogCurrency: null,
            recipientFieldsValid: false,
            recipientFieldsData: {},
            savingFields: false,
        }
    },
    components: {
        RecipientFields
    },
    methods: {
        get_column(val) {
            for(const [k,v] of Object.entries(this.selectedColumns)){
                if(v.name == val || v.value == val )return true
            }
        },
        isUserInGroup: function (type) { 
            return this.helper.isUserInGroup(type, this.$store.getters.StateGroups)
        },
        get_short_id(value){
            return this.helper.get_short_id(value)
        },
        copy(text){
            this.helper.copy_clipboard(text)
            this.$toast.add({ severity: 'info', summary: 'Скопировано', detail: text, life: 1000 })
        },
        formatDate(value) {
            return this.helper.format_date(value)
        },
        detail_page(id, page, new_tab = false) {
            if(new_tab){
                const routeData = this.$router.resolve({path: '/admin/'+page+'/' + id});
                window.open(routeData.href, '_blank');
            } else  this.$router.push({ path: '/admin/'+page+'/' + id })
        },
        openPageWithParams(path, params) {
            const routeData = this.$router.resolve({ path, query: params });
            window.open(routeData.href, '_blank');
        },
        openBlacklistAddPage(recipient) {
            if (!recipient || !recipient.id) return
            const routeData = this.$router.resolve({
                path: '/admin/recipients/blacklist/add/',
                query: {
                    recipient_id: recipient.id,
                    source: 'admin'
                }
            })
            window.open(routeData.href, '_blank')
        },
        goToTelegram(username){
            if(username) window.open('https://t.me/'+username, '_blank')
            return
        },
        onToggle(e) {
            if(e.length>0){
                this.selectedColumns = e
            } else {
                this.selectedColumns = []
            }
        },
        onPage(event) {
            this.lazyParams = event
            this.lazyParams.sort = this.sort
            this.LoadRecipients()
        },
        onSort(event) {
            if(event.sortField && event.sortOrder){
                this.sort = {
                    field: event.sortField,
                    order: event.sortOrder
                }
            } else {
                this.sort = {
                    field: 'date_created',
                    order: -1
                }
            }
            this.lazyParams.sort = this.sort
            this.LoadRecipients()
        },
        onFilter() {
            this.lazyParams.filters = this.filters
            this.LoadRecipients()
        },
        async openEditRecipientFields(recipient) {
            if (!recipient) return

            this.currentRecipient = recipient
            this.recipientFieldsValid = false
            this.recipientFieldsData = recipient.fields ? { ...recipient.fields } : {}
            this.dialogCurrency = recipient.currency_code ? { code: recipient.currency_code } : null

            try {
                const banks = await BankList.GetList(this.$store)
                let bank = null
                if (banks && banks.length) {
                    bank = banks.find(
                        b => b.code === recipient.bank_code && b.type === recipient.payment_system_code
                    )
                }

                if (bank) {
                    this.dialogFields = Array.isArray(bank.recipient_fields) ? bank.recipient_fields : []
                    this.dialogRequiredFields = Array.isArray(bank.recipient_fields_required)
                        ? bank.recipient_fields_required
                        : []
                } else if (recipient.fields && typeof recipient.fields === 'object') {
                    this.dialogFields = Object.keys(recipient.fields)
                    this.dialogRequiredFields = this.dialogFields.slice()
                } else {
                    this.dialogFields = []
                    this.dialogRequiredFields = []
                }

                this.editFieldsDialogVisible = true
            } catch (e) {
                console.error('openEditRecipientFields error', e)
                this.$toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось загрузить настройки банка', life: 5000 })
            }
        },
        closePopup() {
            this.editFieldsDialogVisible = false
        },
        onRecipientFieldsValid(value) {
            this.recipientFieldsValid = !!value
        },
        onRecipientFieldsChange(fields) {
            this.recipientFieldsData = fields || {}
        },
        closeEditFieldsDialog() {
            this.editFieldsDialogVisible = false
            this.currentRecipient = null
            this.dialogFields = []
            this.dialogRequiredFields = []
            this.dialogCurrency = null
            this.recipientFieldsData = {}
        },
        async saveRecipientFields() {
            if (!this.currentRecipient) return
            if (!this.recipientFieldsValid) {
                this.$toast.add({ severity: 'warn', summary: 'Проверка', detail: 'Заполните обязательные поля', life: 4000 })
                return
            }

            this.savingFields = true
            const res = await this.connect.AdminRecipientUpdateStatus(this.currentRecipient.id, {
                fields: this.recipientFieldsData
            }).then(r => r)

            this.savingFields = false

            if (!res || !res.result) {
                this.$toast.add({ severity: 'error', summary: 'Ошибка', detail: res && res.message ? res.message : 'Не удалось сохранить реквизиты', life: 8000 })
                return
            }

            this.$toast.add({ severity: 'success', summary: 'Успешно', detail: 'Реквизиты обновлены', life: 4000 })
            this.closeEditFieldsDialog()
            this.LoadRecipients()
        },
        async LoadRecipients() {
            this.loading = true
            window.scrollTo({ top: 0, behavior: 'smooth' })
            const res = await this.connect.AdminRecipientsList(
                this.lazyParams.rows,
                this.lazyParams.first,
                this.lazyParams.filters,
                this.lazyParams.sort
            ).then(res => res)
            if (!res.result || !res.data) {
                this.$toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Ошибка загрузки данных', life: 10000 })
                this.loading = false
                return
            }
            this.list = res.data
            this.totalRecords = res.total
            this.loading = false
        },
        resetFilter(){
            this.filters = {
                id: { value: '', matchMode: 'contains' },
                user_id: { value: '', matchMode: 'contains' },
                currency_code: { value: '', matchMode: 'contains' },
                bank_code: { value: '', matchMode: 'contains' },
                payment_system_code: { value: '', matchMode: 'contains' },
                confirmed: { value: null, matchMode: FilterMatchMode.EQUALS },
                date_created: { 
                    operator: FilterOperator.AND, 
                    constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] 
                },
                'fields.fullname': { value: '', matchMode: 'contains' },
                'fields.phone_number': { value: '', matchMode: 'contains' },
                'fields.card_number': { value: '', matchMode: 'contains' },
                'fields.account_number': { value: '', matchMode: 'contains' },
                'fields.wallet_address': { value: '', matchMode: 'contains' },
                'fields.wallet_network': { value: '', matchMode: 'contains' },
                'fields.telegram': { value: '', matchMode: 'contains' },
                'fields.bank_name': { value: '', matchMode: 'contains' },
                'fields.address': { value: '', matchMode: 'contains' },
                'fields.city': { value: '', matchMode: 'contains' },
                'fields.country': { value: '', matchMode: 'contains' },
                'fields.swift_code': { value: '', matchMode: 'contains' },
                'fields.iban_number': { value: '', matchMode: 'contains' },
                'fields.upi_id': { value: '', matchMode: 'contains' },
                'fields.wallet_id': { value: '', matchMode: 'contains' },
            }
            this.lazyParams = {
                first: 0,
                rows: 20,
                sortField: null,
                sortOrder: null,
                filters: this.filters,
                sort: this.sort,
            }
            this.LoadRecipients()
        },
    },
    async beforeCreate() {
        this.connect = new Connect()
        this.helper = new Helper()
    },
    mounted() {
        const recipient_id = (this.$route.query.id) ? this.$route.query.id : null
        if(recipient_id) this.filters.id = {value: recipient_id, matchMode: 'contains'}

        const user_id = (this.$route.query.user_id) ? this.$route.query.user_id : null
        if(user_id) this.filters.user_id = {value: user_id, matchMode: 'contains'}

        this.lazyParams = {
            first: 0,
            rows: 20,
            sortField: null,
            sortOrder: null,
            filters: this.filters,
            sort: this.sort,
        }
        this.LoadRecipients()
    }
}
</script>